import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { fetchAWSBlob } from './fetchAWSBlob';

// Downloads multiple ad groups as a zip with a named subfolder per group.
// groups: [{ name: string, keys: string[] }, ...]
export const multiGroupZipKey = async (zipName, groups) => {
  const zip = new JSZip();
  const failedDownloads = [];
  let successfulDownloads = 0;

  await Promise.all(
    groups.map(async (group) => {
      const folderName = (group.name || 'unnamed')
        .replace(/[^a-z0-9 _-]/gi, '')
        .trim();
      const folder = zip.folder(folderName);

      await Promise.all(
        (group.keys || []).map(async (key) => {
          try {
            const blob = await fetchAWSBlob(key);
            folder.file(key, blob, { binary: true });
            successfulDownloads++;
          } catch (error) {
            console.info(`Error downloading ${key}:`, error);
            failedDownloads.push(key);
          }
        })
      );
    })
  );

  if (failedDownloads.length > 0 && successfulDownloads === 0) {
    return { success: false, failedDownloads };
  }

  try {
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${zipName}.zip`);
    await new Promise(resolve => setTimeout(resolve, 1000));
  } catch (error) {
    console.error('Error generating zip:', error);
    throw error;
  }

  return {
    successfulDownloads,
    failedDownloads: failedDownloads.length,
    failedKeys: failedDownloads
  };
};

// Takes in creative data that contains a key into the AWS S3 bucket and downloads each image, zips, them up, and downloads the zip.
export const multiImageZipKey = async (name, keys) => {
  const zip = new JSZip();
  const folder = zip.folder(`${name}`);
  const failedDownloads = [];
  let successfulDownloads = 0;

  await Promise.all(
    keys.map(async (key, i) => {
      try {
        const blob = await fetchAWSBlob(key);
        folder.file(key, blob, { binary: true });
        successfulDownloads++;
      } catch (error) {
        console.info(`Error downloading ${key}:`, error);
        failedDownloads.push(key);
      }
    })
  );

  if (failedDownloads.length > 0) {
    // Inform the user about failed downloads
    return { success: false, failedDownloads };
  }

  try {
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${name}`);

    // Keep the loading state visible for a short time to prevent download spam
    // and provide visual feedback to the user
    await new Promise(resolve => setTimeout(resolve, 1000));
  } catch (error) {
    console.error('Error generating zip:', error);
    throw error;
  }

  return {
    successfulDownloads,
    failedDownloads: failedDownloads.length,
    failedKeys: failedDownloads
  };
};
